/**
 * EVENTSFLO-PROJECT-CLIENT-NAME-DISPLAY-FALLBACK-AUDIT-V1
 *
 * Single source of truth for "what to show in the client column" on a
 * project row. Replaces the legacy `project.client_name || "No client"`
 * idiom that mislabelled every client-linked project as "No client"
 * whenever the backend response had no enriched name.
 *
 * Display policy (in order):
 *   1. If project.client_name is set, return it.
 *   2. Else if a clientsById map resolves project.client_id, return that
 *      client's name.
 *   3. Else if project.client_id exists, return "Client linked — name
 *      missing" + flag the row so admin UIs can surface a "Client link
 *      missing" warning badge for manual review.
 *   4. Else return "No client" with isMissingClientId = true.
 *
 * The function returns an object so callers can drive both the label
 * AND the row badge from one decision point.
 */

export const NO_CLIENT_LABEL = "No client";
export const LINK_MISSING_LABEL = "Client linked — name missing";

/**
 * @param {object} project   - a project row from the API
 * @param {object} [clientsById] - optional { [clientId]: { id, name } }
 * @returns {{
 *   label: string,
 *   isMissingClientId: boolean,
 *   isDanglingClientLink: boolean,
 *   clientId: ?string,
 *   resolvedName: ?string,
 * }}
 */
export function formatProjectClient(project, clientsById = null) {
  if (!project || typeof project !== "object") {
    return {
      label: NO_CLIENT_LABEL,
      isMissingClientId: true,
      isDanglingClientLink: false,
      clientId: null,
      resolvedName: null,
    };
  }

  const clientId = project.client_id || null;

  // 1. Backend-enriched / explicit name on the project row.
  if (project.client_name && String(project.client_name).trim()) {
    return {
      label: String(project.client_name).trim(),
      isMissingClientId: !clientId,
      isDanglingClientLink: false,
      clientId,
      resolvedName: String(project.client_name).trim(),
    };
  }

  // 2. Resolve via a frontend-loaded clients map (e.g. ProjectsPage).
  if (clientId && clientsById && clientsById[clientId]) {
    const name = (clientsById[clientId].name || "").trim();
    if (name) {
      return {
        label: name,
        isMissingClientId: false,
        isDanglingClientLink: false,
        clientId,
        resolvedName: name,
      };
    }
  }

  // 3. Linked but no resolvable name → admins should investigate.
  if (clientId) {
    return {
      label: LINK_MISSING_LABEL,
      isMissingClientId: false,
      isDanglingClientLink: true,
      clientId,
      resolvedName: null,
    };
  }

  // 4. Truly no client_id.
  return {
    label: NO_CLIENT_LABEL,
    isMissingClientId: true,
    isDanglingClientLink: false,
    clientId: null,
    resolvedName: null,
  };
}

export default formatProjectClient;
